const $ = window.jQuery || window.$;

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["loadTrigger", "loadPrevTrigger"];

  static values = {
    url: String,
    nextPage: Number,
    prevPage: Number,
    params: Object
  };

  connect() {
    this.loading = false;
    this.lastScrollAt = performance.now();
    this._stampScroll = () => { this.lastScrollAt = performance.now() };
    document.addEventListener("wheel", this._stampScroll, { passive: true, capture: true });
    document.addEventListener("touchmove", this._stampScroll, { passive: true, capture: true });
    document.addEventListener("keydown", this._stampScroll, { passive: true, capture: true });
    document.addEventListener("scroll", this._stampScroll, { passive: true, capture: true });
    this.observe();
  }

  disconnect() {
    this.unobserve();
    document.removeEventListener("wheel", this._stampScroll, { capture: true });
    document.removeEventListener("touchmove", this._stampScroll, { capture: true });
    document.removeEventListener("keydown", this._stampScroll, { capture: true });
    document.removeEventListener("scroll", this._stampScroll, { capture: true });
  }

  observe() {
    this.unobserve();

    const triggers = [];
    if (this.hasLoadTriggerTarget && this.nextPageValue) triggers.push(this.loadTriggerTarget);
    if (this.hasLoadPrevTriggerTarget && this.prevPageValue) triggers.push(this.loadPrevTriggerTarget);
    if (!triggers.length) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (performance.now() - this.lastScrollAt > 1500) return;
          entry.target.dataset.infiniteListTarget === "loadPrevTrigger" ? this.loadPrevious() : this.loadMore();
        });
      },
      {
        rootMargin: "200px 0px 200px 0px",
        threshold: 0.01
      }
    );

    triggers.forEach((trigger) => this.observer.observe(trigger));
  }

  unobserve() {
    this.observer?.disconnect();
    this.observer = null;
  }

  loadMore() {
    if (!this.nextPageValue) return;

    this.fetchPage(this.nextPageValue, (response) => {
      this.appendContent(response.html);
      this.updatePagination(response.next_page);
    });
  }

  loadPrevious() {
    if (!this.prevPageValue) return;

    this.fetchPage(this.prevPageValue, (response) => {
      this.prependContent(response.html);
      this.updatePreviousPagination(this.prevPageValue - 1);
    });
  }

  fetchPage(page, handle) {
    if (this.loading) return;

    this.loading = true;

    $.ajax({
      url: this.buildUrl(page),
      method: "GET",
      success: handle,
      complete: () => {
        this.loading = false;
        mdFormHelper.hideSpinner();
      },
      dataType: "json"
    });
  }

  appendContent(html) {
    if (!html) return;

    this.loadTriggerTarget.insertAdjacentHTML("beforebegin", html);
    window.dispatchEvent(new CustomEvent("infinite-list:appended"));
  }

  prependContent(html) {
    if (!html) return;

    const container = this.scrollContainer();
    const height = container.scrollHeight;
    this.loadPrevTriggerTarget.insertAdjacentHTML("afterend", html);
    container.scrollTop += container.scrollHeight - height;
    window.dispatchEvent(new CustomEvent("infinite-list:appended"));
  }

  updatePagination(nextPage) {
    this.nextPageValue = nextPage;
    if (!nextPage) this.retire(this.loadTriggerTarget);
  }

  updatePreviousPagination(prevPage) {
    this.prevPageValue = prevPage;
    if (!prevPage) this.retire(this.loadPrevTriggerTarget);
  }

  retire(trigger) {
    this.observer?.unobserve(trigger);
    trigger.remove();
  }

  scrollContainer() {
    let el = this.element;
    while (el && el !== document.body) {
      const overflowY = getComputedStyle(el).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") return el;
      el = el.parentElement;
    }
    return document.scrollingElement;
  }

  buildUrl(page) {
    const baseUrl = new URL(this.urlValue || window.location.href, window.location.origin).toString();
    const params = Object.assign({}, this.paramsValue || {}, { page: page });

    return Object.entries(params).reduce((currentUrl, [key, value]) => {
      if (!value) return currentUrl;

      return urlHandler.updateQueryParam(currentUrl, key, value);
    }, baseUrl);
  }
}
